import { useEffect, useState } from "react";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import {
  useCreateSuperAdminHsn,
  useCreateSuperAdminHsnGroup,
  useSuperAdminHsn,
  useSuperAdminHsnGroup,
  useUpdateSuperAdminHsn,
  useUpdateSuperAdminHsnGroup,
} from "http-hooks/superAdmin/useSuperAdminHsn";

const initialHsn = {
  code_type: "HSN",
  hsncode: "",
  hsndes: "",
  taxrate1: 0,
  amt1: 0,
  taxrate2: 0,
  ag_status: 1,
};

const initialGroup = {
  hsngrpcode: "",
  hsngrpdes: "",
  taxrate1: 0,
  amt1: 0,
  taxrate2: 0,
  ag_status: 1,
  hsndate: new Date().toISOString().slice(0, 10),
};

const codeTypeOptions = [
  { value: "HSN", label: "HSN (Goods)" },
  { value: "SAC", label: "SAC (Services)" },
];

export default function SetHsn({ id, type, onClose }) {
  const isGroup = type === "group";
  const initialData = isGroup ? initialGroup : initialHsn;
  const { data: existingHsn } = useSuperAdminHsn(!isGroup ? id : null);
  const { data: existingGroup } = useSuperAdminHsnGroup(isGroup ? id : null);
  const { mutateAsync: createHsn, isPending: isCreatingHsn } = useCreateSuperAdminHsn();
  const { mutateAsync: updateHsn, isPending: isUpdatingHsn } = useUpdateSuperAdminHsn();
  const { mutateAsync: createGroup, isPending: isCreatingGroup } = useCreateSuperAdminHsnGroup();
  const { mutateAsync: updateGroup, isPending: isUpdatingGroup } = useUpdateSuperAdminHsnGroup();
  const [formData, setFormData] = useState(initialData);

  useEffect(() => {
    const existing = isGroup ? existingGroup : existingHsn;
    if (existing) {
      setFormData({
        ...initialData,
        ...existing,
        hsndate: existing.hsndate?.slice(0, 10) || initialData.hsndate,
      });
    }
  }, [existingHsn, existingGroup, initialData, isGroup]);

  const handleChange = (event) => {
    const { name, value, checked, type: inputType } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: inputType === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { hid, ...data } = formData;
    try {
      if (isGroup) {
        if (id) await updateGroup({ id, data });
        else await createGroup(data);
      } else if (id) {
        await updateHsn({ id, data });
      } else {
        await createHsn(data);
      }
      onClose();
    } catch (error) {
      console.error("HSN save failed:", error);
    }
  };

  const isSaving =
    isCreatingHsn || isUpdatingHsn || isCreatingGroup || isUpdatingGroup;
  const codeField = isGroup ? "hsngrpcode" : "hsncode";
  const descriptionField = isGroup ? "hsngrpdes" : "hsndes";
  const title = `${id ? "Edit" : "Add"} HSN ${isGroup ? "Group" : "Master"}`;

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          name={codeField}
          label={isGroup ? "HSN Group Code *" : `${formData.code_type || "HSN"} Code *`}
          value={formData[codeField]}
          onChange={handleChange}
          maxLength={isGroup ? 2 : 8}
          required
        />
        {!isGroup && (
          <Select
            label="Code Type *"
            options={codeTypeOptions}
            value={codeTypeOptions.find((option) => option.value === formData.code_type)}
            onChange={(option) =>
              setFormData((previous) => ({
                ...previous,
                code_type: option?.value || "HSN",
              }))
            }
            isClearable={false}
            isSearchable={false}
          />
        )}
        {isGroup && (
          <Input
            name="hsndate"
            label="Effective Date *"
            type="date"
            value={formData.hsndate}
            onChange={handleChange}
            required
          />
        )}
        <Input
          name={descriptionField}
          label="Description *"
          value={formData[descriptionField]}
          onChange={handleChange}
          maxLength={40}
          className="sm:col-span-2"
          required
        />
        <Input
          name="taxrate1"
          label="Tax Rate 1 (%) *"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={formData.taxrate1}
          onChange={handleChange}
          required
        />
        <Input
          name="amt1"
          label="Rate Threshold *"
          type="number"
          min="0"
          step="0.01"
          value={formData.amt1}
          onChange={handleChange}
          required
        />
        <Input
          name="taxrate2"
          label="Tax Rate 2 (%) *"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={formData.taxrate2}
          onChange={handleChange}
          required
        />
        <Input
          name="ag_status"
          label="Active"
          type="checkbox"
          value={formData.ag_status === 1}
          onChange={handleChange}
          className="flex items-end pb-2"
        />
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isSaving}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}
